// Leitura padronizada de erros da API.
//
// Cuidado ao usar: o interceptor global de `src/helpers/api.js` NÃO rejeita 401/403/419
// (ele desloga e redireciona), então esses status nunca chegam aos `catch` das telas. Por
// isso o bloqueio de competência fechada responde 423 — um status que continua sendo
// propagado normalmente para o `catch` do componente.
//
// O 423 é compartilhado por três bloqueios, diferenciados SEMPRE pelo `code` do corpo:
//  - CONTRACT_COMPETENCE_CLOSED  — competência fechada (a tela trata);
//  - APPOINTMENT_REPORT_PENDING  — trava de report pendente: o interceptor grava a trava no
//    store, leva o PN para a central de pendências e rejeita com o erro marcado
//    (`isReportPendingError`) — a tela normalmente não precisa mostrar nada;
//  - APPOINTMENT_REPORT_NOT_OPEN — report antes do horário da reunião (a tela mostra
//    "aguardando horário"; nunca redireciona).

export const COMPETENCE_CLOSED_STATUS = 423

export const COMPETENCE_CLOSED_CODE = 'CONTRACT_COMPETENCE_CLOSED'

export const REPORT_LOCK_STATUS = 423

export const REPORT_PENDING_CODE = 'APPOINTMENT_REPORT_PENDING'

export const REPORT_NOT_OPEN_CODE = 'APPOINTMENT_REPORT_NOT_OPEN'

// Nome do erro rejeitado pelo interceptor para a trava (as telas podem checar `error.name`).
export const REPORT_PENDING_ERROR_NAME = 'ReportPendingError'

const responseCode = error => error?.response?.data?.code

const firstValidationMessage = errors => {
  if (!errors || typeof errors !== 'object') return undefined
  const firstField = Object.keys(errors)[0]
  if (!firstField) return undefined
  const messages = errors[firstField]
  if (Array.isArray(messages)) return messages.find(message => typeof message === 'string')

  return typeof messages === 'string' ? messages : undefined
}

/**
 * Extrai a mensagem devolvida pela API, na ordem `message` → `error` → primeira mensagem
 * da sacola de validação do Laravel (422) → `fallback`.
 */
export const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data
  if (!data) return fallback
  if (typeof data === 'string') return data.trim() || fallback
  if (typeof data.message === 'string' && data.message.trim()) return data.message
  if (typeof data.error === 'string' && data.error.trim()) return data.error

  return firstValidationMessage(data.errors) || fallback
}

/**
 * Escrita recusada por competência fechada. O código é conferido além do status para
 * cobrir o caso de um proxy/serviço intermediário reescrever o 423.
 */
export const isCompetenceClosedError = error => {
  const response = error?.response
  if (!response) return false
  const code = responseCode(error)
  if (code === COMPETENCE_CLOSED_CODE) return true
  // Um 423 da trava de report não é competência fechada.
  if (code === REPORT_PENDING_CODE || code === REPORT_NOT_OPEN_CODE) return false

  return response.status === COMPETENCE_CLOSED_STATUS
}

/**
 * Requisição recusada pela trava de report pendente (423 + APPOINTMENT_REPORT_PENDING).
 * O interceptor já gravou a trava e redirecionou; a tela só deve evitar um toast de erro
 * genérico (ou mostrar o texto do corpo, que é o do alerta "Acesso bloqueado").
 */
export const isReportPendingError = error => {
  if (error?.name === REPORT_PENDING_ERROR_NAME) return true

  return error?.response?.status === REPORT_LOCK_STATUS
    && responseCode(error) === REPORT_PENDING_CODE
}

/**
 * Report/rascunho enviado antes do horário da reunião (423 + APPOINTMENT_REPORT_NOT_OPEN).
 * O corpo traz `opens_at` e `server_now` (ISO 8601) para a contagem pelo relógio do servidor.
 */
export const isReportNotOpenError = error => error?.response?.status === REPORT_LOCK_STATUS
  && responseCode(error) === REPORT_NOT_OPEN_CODE

/** `true` quando o corpo do erro traz o `code` informado (qualquer status). */
export const hasApiErrorCode = (error, code) => !!code && responseCode(error) === code

/**
 * Props prontas para o `ToastificationContent` de erro.
 */
export const apiErrorToast = (error, fallback) => ({
  title: 'Oops!',
  text: getApiErrorMessage(error, fallback),
  icon: 'AlertTriangleIcon',
  variant: 'danger',
})
